import {
  DropdownCountry1,
  createCountryStore,
} from '@/components/DropdownCountry'
import MyDatePicker, { useDatePicker } from '@/components/MyDatePicker'
import React, { useMemo } from 'react'
import useSWR from 'swr'
import SkuDropdown, { createSkuIdStore, createPlanIdStore } from './DropdownSku'
import AppSelect, { createSubscribeAppStore } from './app-select'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useColumnFilters } from '@/components/data-table-selectors'
import {
  dnuColumn,
  subDateColumn,
  trialStartColumn,
  trialCanceledColumn,
  trialChurnedColumn,
  trialConvertedColumn,
  subscriptionStartedColumn,
  subscriptionResumedColumn,
  subscriptionChurnedColumn,
  subscriptionCanceledColumn,
  subscriptionPausedColumn,
  subscriptionRefundedColumn,
  subscriptionBillingGraceColumn,
  subscriptionRenewedColumn,
  subscriptionXgradedColumn,
  subscriptionExistingSubscriberColumn,
  totalColumn,
} from './columns'
import SubscribeChart from './subscribe-chart'
import { ChartCodeViewer } from '@/components/zthd/chart-code-viewer'
const CACHE_KEY = 'event1'

const useColumnVisibilityStore = createColumnVisibilityStore({}, CACHE_KEY)
const useCountry = createCountryStore('US', CACHE_KEY)
const useSkuId = createSkuIdStore('', CACHE_KEY)
const usePlanId = createPlanIdStore('', CACHE_KEY)
const useSubscribeApp = createSubscribeAppStore('12', CACHE_KEY)

const columns = [
  subDateColumn,
  dnuColumn,
  totalColumn,
  trialStartColumn,
  trialConvertedColumn,
  subscriptionStartedColumn,
  trialCanceledColumn,
  trialChurnedColumn,
  subscriptionCanceledColumn,
  subscriptionPausedColumn,
  subscriptionResumedColumn,
  subscriptionChurnedColumn,
  subscriptionRefundedColumn,
  subscriptionBillingGraceColumn,
  subscriptionRenewedColumn,
  subscriptionXgradedColumn,
  subscriptionExistingSubscriberColumn,
]

const SubscribeSummary = () => {
  const country = useCountry((state) => state.value)
  const updateCountry = useCountry((state) => state.update)
  const app = useSubscribeApp((state) => state.value)
  const updateApp = useSubscribeApp((state) => state.update)
  const skuId = useSkuId((state) => state.value)
  const updateSkuId = useSkuId((state) => state.update)
  const planId = usePlanId((state) => state.value)
  const updatePlanId = usePlanId((state) => state.update)
  const { startDate, endDate } = useDatePicker((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
  }))

  const { columnVisibility, setColumnVisibility } = useColumnVisibilityStore()
  const { columnFilters } = useColumnFilters()

  const { data: summary } = useSWR([
    '/subscribe/summary',
    { pid: app, country, startDate, endDate, skuId, planId },
  ])

  const { data: skuIds } = useSWR([
    '/subscribe/skuId',
    { pid: app, country },
  ])

  const { data: dnus } = useSWR([
    '/apple/dnus',
    { pid: app, country, startDate, endDate },
  ])

  const skuOptions = useMemo(() => {
    if (!skuIds) {
      return []
    }
    const skuOptions = []
    skuOptions.push({ code: 'all', name: 'all', alias: 'all' })
    skuIds.forEach((skuId) => {
      skuOptions.push({ code: skuId.skuId, name: skuId.skuId, alias: skuId.skuId })
    })
    updateSkuId('all')
    return skuOptions
  }, [skuIds])

  const planOptions = useMemo(() => {
    if (!skuIds) {
      return []
    }
    const planOptions = []
    planOptions.push({ code: 'all', name: 'all', alias: 'all' })
    skuIds.forEach((skuId) => {
      skuId.planIds?.forEach((planId) => {
        planOptions.push({ code: planId, name: planId, alias: planId })
      })
    })
    updatePlanId('all')
    return planOptions
  }, [skuIds])

  const data = useMemo(() => {
    if (!summary) return []
    const summaryData = summary.map((item) => {
      const { eventAt, ...rest } = item
      const dnuDate = eventAt.replaceAll('-', '')
      const dnu = dnus?.find((dnuItem) => dnuItem.date == dnuDate)
      const total = {
        userCount: Object.values(rest).reduce((acc, curr) => acc + (curr?.userCount || 0), 0),
        revenue: Object.values(rest).reduce((acc, curr) => acc + (curr?.revenue || 0), 0),
      }

      return {
        eventAt,
        dnu: dnu?.total,
        total,
        ...rest,
      }
    })

    return summaryData
  }, [summary, dnus])


  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableMultiRowSelection: true,
    state: {
      columnFilters,
      columnVisibility,
    },
  })

  return (
    <>
      <div className="flex items-center gap-4 py-4 sm:gap-2 sm:py-2 flex-wrap">
        <AppSelect value={app} onValueChange={updateApp} />
        <DropdownCountry1 value={country} update={updateCountry} />
        <MyDatePicker getMaxDate="complete" />
        <SkuDropdown value={skuId} update={updateSkuId} datas={skuOptions} labelName="Sku" />
        {planOptions.length > 1 && <SkuDropdown value={planId} update={updatePlanId} datas={planOptions} labelName="Plan" />}
        <ChartCodeViewer>
          <SubscribeChart data={data} />
        </ChartCodeViewer>

        <DataTableViewOptions table={table} />
      </div>
      <DataTableWithBorder table={table} />
    </>
  )
}

export default SubscribeSummary
